import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  Building2,
  MapPin,
  ArrowRight
} from 'lucide-react';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuth();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // عرض رسالة إذا تم تعليق الحساب
  useEffect(() => {
    if (searchParams.get('suspended') === 'true') {
      toast.error('حسابك معلق. يرجى التواصل مع الدعم الفني.');
      // مسح الباراميتر من URL
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams, toast]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = t('auth.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('errors.invalidEmail');
    }

    if (!formData.password) {
      newErrors.password = t('auth.passwordRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        toast.success('تم تسجيل الدخول بنجاح');
        navigate('/dashboard');
        return;
      }

      const errorMessage = result.error || t('auth.invalidCredentials');

      // Set field-specific errors when backend provides an errorCode
      if (result.errorCode === 'USER_NOT_FOUND') {
        setErrors({ email: errorMessage });
      } else if (result.errorCode === 'INVALID_PASSWORD') {
        setErrors({ password: errorMessage });
      } else if (result.errorCode === 'ACCOUNT_SUSPENDED') {
        // لا تعرض toast هنا، سيظهر من خلال redirect
        setErrors({ form: errorMessage });
        // إعادة توجيه مع باراميتر لعرض الرسالة
        navigate('/login?suspended=true');
        return;
      } else {
        setErrors({ form: errorMessage });
      }

      toast.error(errorMessage);
      setLoading(false);
    } catch (error) {
      toast.error(error.message || 'حدث خطأ في الخادم');
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="bg-pattern"></div>
        <div className="bg-overlay"></div>
      </div>
      
      <div className="login-content">
        <div className="login-left">
          <div className="brand-section">
            <div className="brand-logo">
              <Building2 size={48} />
            </div>
            <h1>{t('common.appFullName')}</h1>
            <p>منصتك الذكية لإدارة العروض والطلبات العقارية</p>
          </div>
          
          <div className="features-list">
            <div className="feature-item">
              <div className="feature-icon">
                <MapPin size={24} />
              </div>
              <div className="feature-text">
                <h3>إدارة العروض</h3>
                <p>أضف وأدر عروضك العقارية بسهولة</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <Building2 size={24} />
              </div>
              <div className="feature-text">
                <h3>شبكة المسوقين</h3>
                <p>شارك واكتشف عروض المسوقين الآخرين</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <ArrowRight size={24} />
              </div>
              <div className="feature-text">
                <h3>نظام المطابقة</h3>
                <p>اربط العروض بالطلبات تلقائياً</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="login-right">
          <div className="login-card">
            <div className="login-header">
              <h2>{t('auth.loginTitle')}</h2>
              <p>{t('auth.loginSubtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="email">
                  <Mail size={18} />
                  {t('auth.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-control ${errors.email ? 'error' : ''}`}
                  placeholder="example@email.com"
                  autoComplete="email"
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="password">
                  <Lock size={18} />
                  {t('auth.password')}
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`form-control ${errors.password ? 'error' : ''}`}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && <span className="error-text">{errors.password}</span>}
              </div>

              <div className="form-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                  />
                  <span>{t('auth.rememberMe')}</span>
                </label>
                <Link to="/forgot-password" className="forgot-link">
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <button 
                type="submit" 
                className="btn-login"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    {t('auth.loggingIn')}
                  </>
                ) : (
                  t('auth.loginButton')
                )}
              </button>

              <div className="register-link">
                <span>{t('auth.noAccount')}</span>
                <Link to="/register">{t('auth.register')}</Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
